"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Mail, Phone, MapPin, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactUs() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contact-us" ref={sectionRef} className="bg-dark-section text-dark-foreground py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className={`grid md:grid-cols-2 gap-12 lg:gap-16 transition-all duration-[var(--duration-medium)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          
          <div className="space-y-12">
            <div className="space-y-4">
              <h2 className="text-[36px] leading-[1.3] font-semibold tracking-[-0.2px] !text-[hsl(210,17%,98%)]">
                Contact Us
              </h2>
              <p className="text-[16px] leading-[1.6] !text-[hsl(210,17%,98%)]">
                Get in touch with our team. We're here to help with all your accounting needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-6">
                <div className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 size-12 rounded-[var(--radius-md)] flex items-center justify-center border border-accent/20 transition-all duration-[var(--duration-medium)] group-hover:bg-accent/20 !text-[hsl(63,75%,51%)] !bg-[hsl(63,75%,51%)]">
                    <Mail className="size-5 text-accent-dark" />
                  </div>
                  <div>
                    <h4 className="text-[18px] font-semibold mb-1 !text-[hsl(210,17%,98%)]">Email</h4>
                    <p className="text-muted-foreground">mail@sloane.co.uk</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 size-12 rounded-[var(--radius-md)] flex items-center justify-center border border-accent/20 transition-all duration-[var(--duration-medium)] group-hover:bg-accent/20 !bg-[hsl(63,75%,51%)]">
                    <Phone className="size-5 text-accent-dark" />
                  </div>
                  <div>
                    <h4 className="text-[18px] font-semibold mb-1 !text-[hsl(210,17%,98%)]">Phone</h4>
                    <p className="text-muted-foreground">+44 20 7221 3292</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 size-12 rounded-[var(--radius-md)] flex items-center justify-center border border-accent/20 transition-all duration-[var(--duration-medium)] group-hover:bg-accent/20 !bg-[hsl(63,75%,51%)]">
                    <MapPin className="size-5 text-accent-dark" />
                  </div>
                  <div>
                    <h4 className="text-[18px] font-semibold mb-1 !text-[hsl(210,17%,98%)]">Address</h4>
                    <p className="text-muted-foreground">
                      Office 015, <br />
                      The Metal Box Factory,<br />
                      30 Great Guildford Street,<br />
                      Borough, <br />
                      London, <br />
                      SE1 0HS
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-[var(--radius-sm)] overflow-hidden border border-border/40 shadow-soft bg-surface/5 backdrop-blur-sm">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.518604724831!2d-0.09633642337777083!3d51.50326461041187!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487604b0b7692131%3A0x6b86b45a0b73523e!2sThe%20Metal%20Box%20Factory!5e0!3m2!1sen!2suk!4v1710345678901!5m2!1sen!2suk" width="100%" height="300" style={{
                  border: 0
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Office Location Map" className="w-full" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <FormProvider className="bg-surface/5 backdrop-blur-sm border border-border/20 rounded-[var(--radius-sm)] p-6 md:p-8 shadow-soft" formId="contact_form_teamandcontact" sectionName="contact-us" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            subject: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for contacting us! We'll get back to you soon." errorMessage="Failed to send your message. Please try again.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/10 border border-accent/30 rounded-[var(--radius-sm)]">
        <p className="text-foreground font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must be less than 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} className="bg-background/80 border-border/40 focus-visible:border-primary focus-visible:ring-primary/30" />
                </FormControl>
                <FormDescription>Enter your full name.</FormDescription>
                <FormMessage />
              </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} className="bg-background/80 border-border/40 focus-visible:border-primary focus-visible:ring-primary/30" />
                </FormControl>
                <FormDescription>We'll use this to reply to your inquiry.</FormDescription>
                <FormMessage />
              </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+44 1234 567890" {...field} className="bg-background/80 border-border/40 focus-visible:border-primary focus-visible:ring-primary/30" />
                </FormControl>
                <FormDescription>Optional - in case we need to call you.</FormDescription>
                <FormMessage />
              </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Subject is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel>Subject</FormLabel>
                <FormControl>
                  <select {...field} className="flex h-9 w-full rounded-md border border-border/40 bg-background/80 px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:ring-[3px] !text-[hsl(0,0%,0%)] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                    <option value="">Select a subject</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Accounting Services">Accounting Services</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Other">Other</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us how we can help..." rows={5} {...field} className="aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex field-sizing-content min-h-16 w-full rounded-md border px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] bg-background/80 border-border/40 focus-visible:border-primary focus-visible:ring-primary/30 resize-none !text-[hsl(0,0%,0%)] placeholder:text-muted-foreground dark:aria-invalid:ring-destructive/40 dark:bg-input/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" />
                </FormControl>
                <FormDescription>Provide as many details as possible.</FormDescription>
                <FormMessage />
              </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-[var(--radius-sm)]">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-[var(--radius-full)] bg-primary text-foreground-primary hover:bg-primary-dark transition-all duration-[var(--duration-fast)] shadow-soft hover:shadow-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}